import React, { FC, HTMLAttributeAnchorTarget } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Anchor.module.css';
import { useData } from '@hooks/useData';

export enum AnchorMode {
    primary = 'primary',
    secondary = 'secondary',
    light = 'light',
    danger = 'danger',
    link = 'link',
    underline = 'underline',
    none = '',
}

export interface AnchorProps {
    id?: string;
    classes?: string;
    href?: string;
    mailTo?: string;
    tel?: string;
    children: JSX.Element | JSX.Element[] | string;
    icon?: string;
    iconPosition?: 'left' | 'farLeft';
    mode: AnchorMode;
    size?: 'sm' | 'md';
    outline?: boolean;
    disabled?: boolean;
    textColor?: 'light'; // may be used with mode 'secondary'
    bgColor?: 'blue'; // may be used with mode 'primary'
    tall?: boolean; // may be used with small buttons
    target?: HTMLAttributeAnchorTarget;
    rel?: string;
    onClick?: (e: React.MouseEvent) => void;
}

const AnchorComponent: FC<AnchorProps> = ({
    id,
    classes,
    href,
    mailTo,
    tel,
    children,
    icon,
    iconPosition,
    mode,
    size,
    outline,
    disabled,
    textColor,
    bgColor,
    tall,
    target,
    rel,
    onClick,
}) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useThemes } = require('@hooks/useThemes');
    const { useGrayscaleTheme } = useThemes();
    const { i18n } = useTranslation();
    const { allSettings } = useData();

    const isAnimationActive = allSettings?.data?.layouts?.find((item) => item.name === 'buttonsAnimations')?.value === '1';

    const overrideTextColorAccordingToSettings = String(
        allSettings?.data?.layouts?.find((item) => item.name === 'colorTextButtons')?.value
    );

    const commonClasses =
        // mode + outline
        `${getModeClasses(mode, outline, isAnimationActive)}` +
        // size
        ` ${size === 'sm' ? ` btn-sm ${styles['btn-sm']} ${tall ? styles['btn-sm-tall'] : ''}` : ''}` +
        // override text color
        ` ${textColor === 'light' ? styles['btn-text-light'] : ''}` +
        // override bg color
        ` ${bgColor === 'blue' ? styles['btn-bg-blue'] : ''}` +
        // override icon position
        ` ${iconPosition === 'farLeft' ? styles['far-left-icon'] : ''}` +
        // themes
        ` ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
        // extra classes
        ` ${classes || ''}` +
        ` ${disabled ? styles['disabled'] : ''}`;

    const link = mailTo
        ? `mailto:${mailTo}`
        : tel
          ? `tel:${tel}`
          : href?.startsWith('http')
            ? href
            : `/${i18n.language === (i18n.options?.fallbackLng || '') ? href : `${i18n.language}/${href}`}`;

    const handleClick = (e: React.MouseEvent) => {
        if (disabled) {
            e.preventDefault();
            e.stopPropagation();
        } else {
            onClick && onClick(e);
        }
    };

    const styleOverride: React.CSSProperties = {};
    if (overrideTextColorAccordingToSettings && isAnimationActive == false) {
        if ((mode === AnchorMode.primary || mode === AnchorMode.secondary) && outline != true) {
            styleOverride['color'] = overrideTextColorAccordingToSettings;
        } else if (mode === AnchorMode.light || outline == true) {
            styleOverride['color'] = 'var(--buttons-color, var(--primaryBlue))';
        }
    }

    return (
        <Link
            to={link}
            id={id || undefined}
            className={commonClasses}
            onClick={handleClick}
            target={target}
            style={styleOverride}
            rel={rel}
            data-testid="anchor">
            {icon && <i className={icon} />} {children}
        </Link>
    );
};

function getModeClasses(mode: AnchorMode, outline?: boolean, usePrimaryMode?: boolean): string {
    /* overrides btn settings*/

    if (!usePrimaryMode && mode === AnchorMode.secondary) {
        mode = AnchorMode.primary;
    }
    if (!usePrimaryMode && mode === AnchorMode.light) {
        return `btn ${styles.btn} ${styles['btn-light']} ${styles['btn-light-no-animation']}`;
    }

    /* end of override */

    if (mode === AnchorMode.underline) {
        return 'text-reset text-decoration-underline';
    }
    if (mode === AnchorMode.none) {
        return styles.basic;
    }
    return `btn ${styles.btn} ${styles[`btn-${outline ? 'outline-' : ''}${mode}`]}`;
}

export const Anchor = AnchorComponent;
